import React from 'react';

import { Autocomplete, Box, Button, Checkbox, Chip, Stack, TextField, Typography } from '@mui/material';

import {
  DEFAULT_ITEM_STATUS,
  ITEM_STATUSES,
  OPTION_LIMIT,
  SELECTION_FILTERS,
  type SelectionId,
  type Selections,
  activeSelectionCount,
} from './filterSelections';

interface SelectionFieldProps {
  label: string;
  options: ReadonlyArray<string>;
  value: Array<string>;
  loading: boolean;
  placeholder: string;
  onChange: (values: Array<string>) => void;
}

function SelectionField({ label, options, value, loading, placeholder, onChange }: SelectionFieldProps) {
  // Lens lists at most OPTION_LIMIT values per control, so a long list also accepts a typed value.
  const capped = options.length >= OPTION_LIMIT;
  const choices = [...new Set([...options.filter(Boolean), ...value])];
  return (
    <Autocomplete
      multiple
      freeSolo={capped}
      disableCloseOnSelect
      size="small"
      limitTags={1}
      loading={loading}
      options={choices}
      value={value}
      onChange={(_, values) => onChange(values.map(String).filter(Boolean))}
      renderTags={(values, getTagProps) =>
        values.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} {...tagProps} label={option} size="small" />;
        })
      }
      renderOption={({ key: _key, ...optionProps }, option, { selected }) => (
        <Box component="li" {...optionProps} key={option}>
          <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
          {option}
        </Box>
      )}
      renderInput={(inputProps) => (
        <TextField
          {...inputProps}
          label={label}
          placeholder={value.length ? undefined : placeholder}
          helperText={
            capped ? `First ${OPTION_LIMIT} values. Type an exact value and press Enter to add another.` : undefined
          }
        />
      )}
      sx={{ minWidth: 0, '& .MuiAutocomplete-tag': { maxWidth: 'calc(100% - 45px)' } }}
    />
  );
}

interface Props {
  selections: Selections;
  itemStatus: Array<string>;
  options: Record<string, Array<string>>;
  loading: boolean;
  failed: boolean;
  onChange: (next: Selections) => void;
  onItemStatusChange: (next: Array<string>) => void;
  onClear: () => void;
}

export default function ProductDashSelections({
  selections,
  itemStatus,
  options,
  loading,
  failed,
  onChange,
  onItemStatusChange,
  onClear,
}: Props) {
  const active = activeSelectionCount(selections, itemStatus);
  const select = (id: SelectionId, values: Array<string>) => onChange({ ...selections, [id]: values });
  return (
    <Stack gap={1} sx={{ mt: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
        <SelectionField
          label="Item Status"
          options={options.item_status?.length ? options.item_status : [...ITEM_STATUSES]}
          value={itemStatus}
          loading={false}
          placeholder="All"
          onChange={onItemStatusChange}
        />
        {SELECTION_FILTERS.map(({ id, label }) => (
          <SelectionField
            key={id}
            label={label}
            options={options[id] ?? []}
            value={selections[id] ?? []}
            loading={loading}
            placeholder="All"
            onChange={(values) => select(id, values)}
          />
        ))}
      </Box>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="caption" color="text.secondary">
          {failed
            ? 'Filter values could not be loaded. Selections in the link still apply.'
            : active
              ? `${active} quick filter${active === 1 ? '' : 's'} changed from the default. Reset sets every control back to All and Item Status back to ${DEFAULT_ITEM_STATUS.join(' and ')}.`
              : `All values, with Item Status ${DEFAULT_ITEM_STATUS.join(' and ')}. Choose values above to narrow every sheet.`}
        </Typography>
        {active ? (
          <Button variant="text" size="small" onClick={onClear}>
            Reset quick filters
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
